/**
 * User Preferences Cookie Manager
 * 
 * Stocke les préférences utilisateur dans un cookie quand l'utilisateur est connecté.
 * Permet de :
 * - Sauvegarder les préférences dans un cookie (en plus de localStorage + Supabase)
 * - Restaurer les préférences depuis le cookie au login
 * - Supprimer le cookie au logout → l'UI revient aux défauts
 * - Émettre des événements pour que l'UI réagisse aux changements d'auth
 * 
 * Cookie strictement fonctionnel (pas de tracking) → pas de consentement RGPD requis.
 */

const PREFS_COOKIE_NAME = 'mpt_user_prefs';
const PREFS_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 an
const SHARED_COOKIE_DOMAIN = '.magicparktimes.com';

// Événements custom pour la communication auth → UI
export const AUTH_EVENTS = {
  SIGNED_OUT: 'mpt:auth:signed-out',
  SIGNED_IN: 'mpt:auth:signed-in',
  PREFERENCES_RESTORED: 'mpt:preferences:restored',
};

// Clés de préférences synchronisées (même liste que SYNCED_PREFERENCE_KEYS dans AuthContext)
const COOKIE_PREFERENCE_KEYS = [
  'theme',
  'highContrast',
  'showSnow',
  'i18nextLng',
  'language',
  'showLocalTime',
  'showInfoButtons',
  'hideNavLabels',
  'liquidGlassEnabled',
  'soundEnabled',
  'allParksFavorites',
  'favoritesInfo',
  'favoriteParkIds',
  'attractionSortPreference',
  'restaurantSortPreference',
  'historyDisplayTz',
  'showAsia',
  'hideImportantNotice',
  'hideImportantNoticeAsterix',
  'parkViewMode_DLP',
  'parkViewMode_WDS',
];

// Valeurs par défaut quand l'utilisateur se déconnecte
const DEFAULT_PREFERENCES = {
  theme: null, // null = suivre la préférence système
  highContrast: 'false',
  showSnow: 'false',
  showLocalTime: 'false',
  showInfoButtons: 'true',
  hideNavLabels: 'false',
  liquidGlassEnabled: 'false',
  soundEnabled: 'true',
  showAsia: null,
};

/**
 * Vérifie si une clé est une préférence dynamique (parkOrder_*, newsBlock_*)
 */
const isDynamicPreferenceKey = (key) => {
  if (!key || typeof key !== 'string') return false;
  return key.startsWith('parkOrder_') || key.startsWith('newsBlock_');
};

/**
 * Vérifie si une clé est une préférence à synchroniser
 */
export const isPreferenceKey = (key) => {
  return COOKIE_PREFERENCE_KEYS.includes(key) || isDynamicPreferenceKey(key);
};

// ─── Cookie helpers ──────────────────────────────────────────────────────────

const shouldUseSharedCookieDomain = () => {
  try {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;
    return hostname === 'magicparktimes.com'
      || hostname === 'www.magicparktimes.com'
      || hostname.endsWith('.magicparktimes.com');
  } catch {
    return false;
  }
};

const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [cookieName, ...parts] = cookie.split('=');
    if (cookieName.trim() === name) {
      return parts.join('=').trim();
    }
  }
  return null;
};

const setCookie = (name, value, maxAge) => {
  if (typeof document === 'undefined') return;
  const isSecure = typeof window !== 'undefined' &&
    typeof window.location !== 'undefined' &&
    window.location.protocol === 'https:';

  const parts = [
    `${name}=${value}`,
    'path=/',
    `max-age=${maxAge}`,
    'SameSite=Lax',
  ];
  if (isSecure) parts.push('Secure');
  if (shouldUseSharedCookieDomain()) parts.push(`Domain=${SHARED_COOKIE_DOMAIN}`);
  document.cookie = parts.join('; ');
};

const deleteCookie = (name) => {
  if (typeof document === 'undefined') return;
  const isSecure = typeof window !== 'undefined' &&
    typeof window.location !== 'undefined' &&
    window.location.protocol === 'https:';

  const baseParts = [
    `${name}=`,
    'path=/',
    'max-age=0',
    'SameSite=Lax',
  ];
  if (isSecure) baseParts.push('Secure');
  document.cookie = baseParts.join('; ');
  if (shouldUseSharedCookieDomain()) {
    document.cookie = [...baseParts, `Domain=${SHARED_COOKIE_DOMAIN}`].join('; ');
  }
};

const encodePrefs = (obj) => {
  try {
    const json = JSON.stringify(obj);
    if (typeof globalThis !== 'undefined' && typeof globalThis.btoa === 'function') {
      return globalThis.btoa(unescape(encodeURIComponent(json)));
    }
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(json, 'utf8').toString('base64');
    }
    return encodeURIComponent(json);
  } catch {
    return '';
  }
};

const decodePrefs = (encoded) => {
  try {
    let json;
    if (typeof globalThis !== 'undefined' && typeof globalThis.atob === 'function') {
      json = globalThis.atob(encoded);
      try { json = decodeURIComponent(escape(json)); } catch { /* ignore */ }
    } else if (typeof Buffer !== 'undefined') {
      json = Buffer.from(encoded, 'base64').toString('utf8');
    } else {
      json = decodeURIComponent(encoded);
    }
    return JSON.parse(json);
  } catch {
    return null;
  }
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Sauvegarde les préférences actuelles du localStorage dans le cookie.
 * Appelé quand un utilisateur est connecté et qu'une préférence change.
 */
export const savePreferencesToCookie = () => {
  try {
    const prefs = {};
    // Clés statiques
    for (const key of COOKIE_PREFERENCE_KEYS) {
      const value = localStorage.getItem(key);
      if (value !== null) {
        prefs[key] = value;
      }
    }
    // Clés dynamiques (parkOrder_*, newsBlock_*)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (isDynamicPreferenceKey(key)) {
        prefs[key] = localStorage.getItem(key);
      }
    }

    const encoded = encodePrefs(prefs);
    if (encoded) {
      setCookie(PREFS_COOKIE_NAME, encoded, PREFS_COOKIE_MAX_AGE);
    }
  } catch (e) {
    console.warn('[UserPrefsCookie] Erreur sauvegarde cookie:', e);
  }
};

/**
 * Lit les préférences depuis le cookie.
 * @returns {Object|null} Les préférences ou null si pas de cookie
 */
export const readPreferencesFromCookie = () => {
  try {
    const raw = getCookie(PREFS_COOKIE_NAME);
    if (!raw) return null;
    return decodePrefs(raw);
  } catch {
    return null;
  }
};

/**
 * Supprime le cookie de préférences.
 * Appelé au signOut.
 */
export const clearPreferencesCookie = () => {
  deleteCookie(PREFS_COOKIE_NAME);
};

/**
 * Restaure les préférences depuis un objet (cookie ou Supabase) vers le localStorage.
 * Met à jour le localStorage puis émet un événement pour que l'UI se mette à jour.
 * @param {Object} prefs - Les préférences à restaurer
 */
export const restorePreferencesToLocalStorage = (prefs) => {
  if (!prefs || typeof prefs !== 'object') return;

  for (const [key, value] of Object.entries(prefs)) {
    if (!isPreferenceKey(key)) continue;
    try {
      if (value !== null && value !== undefined) {
        localStorage.setItem(key, value);
      }
    } catch {
      // ignore
    }
  }
};

/**
 * Réinitialise les préférences du localStorage aux valeurs par défaut.
 * Appelé au signOut pour que l'UI revienne à l'état initial.
 */
export const resetPreferencesToDefaults = () => {
  for (const [key, defaultValue] of Object.entries(DEFAULT_PREFERENCES)) {
    try {
      if (defaultValue === null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, defaultValue);
      }
    } catch {
      // ignore
    }
  }

  // Supprimer les clés dynamiques
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (isDynamicPreferenceKey(key)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
};

/**
 * Émet un événement custom pour signaler un changement d'état d'authentification.
 * @param {string} eventName - Nom de l'événement (AUTH_EVENTS.*)
 * @param {Object} [detail] - Données supplémentaires
 */
export const emitAuthEvent = (eventName, detail = {}) => {
  if (typeof window === 'undefined') return;

  const dispatch = () => {
    // eslint-disable-next-line no-undef
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  };

  if (typeof globalThis.queueMicrotask === 'function') {
    globalThis.queueMicrotask(dispatch);
    return;
  }

  setTimeout(dispatch, 0);
};

/**
 * Écoute un événement custom d'authentification.
 * @param {string} eventName - Nom de l'événement
 * @param {Function} handler - Callback
 * @returns {Function} Fonction de nettoyage pour le useEffect
 */
export const onAuthEvent = (eventName, handler) => {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(eventName, handler);
  return () => window.removeEventListener(eventName, handler);
};
